import { useMemo } from "react";
import { Link } from "react-router-dom";

import Badge from "../components/core/Badge";

import { useAuthInfo } from "../context/AuthContext";
import { formatPhone } from "../util/stringUtils";
import { checkEditAccess } from "../helpers/checkAccess";

const useDataColumns = () => {
  const { userInfo } = useAuthInfo();

  const isSuperAdmin =
    userInfo.role.name === "admin" || userInfo.role.name === "super-admin";

  const userColumns = useMemo(
    () => ({
      first_name: {
        id: "first_name",
        name: "Name",
        selector: (row) => row.first_name,
        sortable: true,
        cell: (row) => (
          <Link
            className="table-link truncate"
            to={{ pathname: `/user/${row.user_id}` }}
          >
            {row.first_name} {row.last_name}
          </Link>
        ),
      },
      email: {
        id: "email",
        name: "Email",
        selector: (row) => row.email,
        sortable: true,
      },
      phone: {
        id: "phone",
        name: "Phone",
        selector: (row) => row.phone,
        sortable: false,
        cell: (row) => formatPhone(row.phone),
      },
      orgName: {
        id: "orgName",
        name: "Org",
        selector: (row) => row.organization.name,
        sortable: true,
      },
      companyName: {
        id: "companyName",
        name: "Company",
        selector: (row) => row.companies?.[0]?.name || "-",
        sortable: true,
      },
      companyRole: {
        id: "companyRole",
        name: "Company Role",
        selector: (row) => row.companies?.[0]?.role || "-",
        sortable: true,
      },
      role: {
        id: "role",
        name: "Role",
        selector: (row) => row.role?.name,
        sortable: true,
      },
      is_active: {
        id: "is_active",
        name: "Active",
        selector: (row) => row.is_active,
        sortable: true,
        cell: (row) => <Badge status={row.is_active} />,
      },
      edit_button: {
        id: "edit_button",
        name: "",
        sortable: false,
        style: {
          justifyContent: "flex-end",
        },
        cell: (row) => (
          <>
            {checkEditAccess(userInfo, row) ? (
              <Link to={{ pathname: `/user-add/${row.user_id}` }}>
                <button>Edit</button>
              </Link>
            ) : null}
          </>
        ),
      },
      user_id: {
        id: "user_id",
        name: "ID",
        selector: (row) => row.user_id,
        sortable: false,
      },
    }),
    [userInfo]
  );

  const organizationColumns = useMemo(
    () => ({
      name: {
        id: "name",
        name: "Name",
        selector: (row) => row.name,
        sortable: true,
        cell: (row) => (
          <Link
            className="table-link truncate"
            to={{ pathname: `/organization/${row.org_id}` }}
          >
            {row.name}
          </Link>
        ),
      },
      city: {
        id: "city",
        name: "City",
        selector: (row) => row.city,
        sortable: true,
      },
      state: {
        id: "state",
        name: "State",
        selector: (row) => row.state,
        sortable: true,
      },
      phone: {
        id: "phone",
        name: "Phone",
        selector: (row) => row.phone,
        sortable: true,
        cell: (row) => formatPhone(row.phone),
      },
      org_id: {
        id: "org_id",
        name: "ID",
        selector: (row) => row.org_id,
        sortable: false,
      },
      is_active: {
        id: "is_active",
        name: "Active",
        selector: (row) => row.is_active,
        sortable: true,
        cell: (row) => <Badge status={row.is_active} />,
        width: "150px",
      },
    }),
    [isSuperAdmin]
  );

  return { userColumns, organizationColumns };
};

export default useDataColumns;
